import React, { useState, useRef, useEffect, useCallback } from "react"
import ReactDOM from "react-dom"

// A brand-styled dropdown that replaces the native <select>.
//
// The open menu is rendered in a portal on <body> with position:fixed, so it can
// NEVER be clipped by an ancestor's `overflow:hidden` (cards, .main-content,
// table-responsive wrappers, etc.). It opens directly below the toggle by default
// and only flips above when there genuinely isn't room below (field near the page
// bottom); its height is capped to the space actually available so the last
// options are always reachable (you scroll inside the menu). Closes on outside
// click / Escape and re-positions on scroll / resize. Options: { label, value }.
const MENU_CAP = 260 // px, matches .mw-select-menu max-height
const MIN_ROOM = 150 // px below the toggle before we consider flipping up

const MwSelect = (props: any) => {
  const { value, options = [], onChange, placeholder = "Select", disabled } = props
  const [open, setOpen] = useState(false)
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})
  const ref = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const selected = options.find((o: any) => o.value === value)

  // Compute the fixed-position box for the portalled menu based on the toggle's
  // viewport rect and the space available above / below it.
  const place = useCallback(() => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const margin = 8
    const vh = window.innerHeight
    const spaceBelow = vh - r.bottom - margin
    const spaceAbove = r.top - margin
    const dropUp = spaceBelow < MIN_ROOM && spaceAbove > spaceBelow
    const room = dropUp ? spaceAbove : spaceBelow
    const maxHeight = Math.max(120, Math.min(MENU_CAP, room))
    const base: React.CSSProperties = {
      position: "fixed",
      left: Math.round(r.left),
      width: Math.round(r.width),
      right: "auto",
      maxHeight,
      zIndex: 4000, // above reactstrap modals (~1050)
    }
    setMenuStyle(
      dropUp
        ? { ...base, top: "auto", bottom: Math.round(vh - r.top + 4) }
        : { ...base, top: Math.round(r.bottom + 4), bottom: "auto" }
    )
  }, [])

  useEffect(() => {
    if (!open) return
    place()
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      // ignore clicks on the toggle or inside the portalled menu
      if (ref.current && ref.current.contains(t)) return
      if (menuRef.current && menuRef.current.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    // reposition (or nothing to follow) if the page scrolls or resizes
    window.addEventListener("resize", place)
    window.addEventListener("scroll", place, true)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
      window.removeEventListener("resize", place)
      window.removeEventListener("scroll", place, true)
    }
  }, [open, place])

  const menu = open
    ? ReactDOM.createPortal(
        <div className="mw-select-menu" ref={menuRef} style={menuStyle}>
          {options.map((op: any, i: number) => (
            <button
              type="button"
              key={i}
              className={`mw-select-item ${op.value === value ? "active" : ""}`}
              onClick={() => {
                onChange(op.value)
                setOpen(false)
              }}
            >
              <span>{op.label}</span>
              {op.value === value && <i className="mdi mdi-check mw-select-check" />}
            </button>
          ))}
        </div>,
        document.body
      )
    : null

  return (
    <div className={`mw-select ${open ? "show" : ""}`} ref={ref}>
      <button
        type="button"
        className="mw-select-toggle"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
      >
        <span className={selected ? "" : "mw-select-placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <i className="mdi mdi-chevron-down" />
      </button>
      {menu}
    </div>
  )
}

export default MwSelect
